import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/App';
import { useApiData } from '@/hooks';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { 
  Trophy, Medal, Star, Award, Users, TrendingUp, Calendar,
  Crown, Target, Sparkles, ChevronRight, RefreshCw, BarChart3, Activity
} from 'lucide-react';
import { PageHeader } from '@/components/ModernUI';
import DataFixToolbar from '@/components/common/DataFixToolbar';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, BarChart, Bar } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Recognition Badge Component
const RecognitionBadge = ({ tier, size = 'md' }) => {
  const badges = {
    'Gold Star Leader': { icon: '🥇', bg: 'bg-gradient-to-r from-yellow-400 to-amber-500', glow: 'shadow-amber-500/50', text: 'text-amber-900' },
    'Silver Excellence': { icon: '🥈', bg: 'bg-gradient-to-r from-gray-300 to-slate-400', glow: 'shadow-slate-500/50', text: 'text-slate-800' },
    'Bronze Achievement': { icon: '🥉', bg: 'bg-gradient-to-r from-orange-400 to-amber-600', glow: 'shadow-orange-500/50', text: 'text-orange-900' },
    'Top Performer': { icon: '⭐', bg: 'bg-gradient-to-r from-blue-400 to-indigo-500', glow: 'shadow-blue-500/50', text: 'text-white' }
  };
  
  const badge = badges[tier] || badges['Top Performer'];
  const sizeClasses = size === 'lg' ? 'px-4 py-2 text-sm' : 'px-2 py-1 text-xs';
  
  return (
    <span 
      className={`${badge.bg} ${badge.text} ${sizeClasses} rounded-full font-bold inline-flex items-center gap-1 shadow-lg ${badge.glow}`}
      style={{ boxShadow: `0 0 15px ${badge.glow}` }}
    >
      {badge.icon} {tier}
    </span>
  );
};

// Weekly Leaders Component - Refactored with hooks
const WeeklyLeaders = ({ token }) => {
  const { data: leaders, loading, refetch } = useApiData('/api/dashboard/recognition/weekly-leaders?top_n=5', {
    cache: true,
    cacheKey: 'weekly_leaders'
  });

  if (loading) return <Skeleton className="h-96 w-full" />;
  if (!leaders || !leaders.leaders?.length) {
    return (
      <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700">
        <CardContent className="p-12 text-center">
          <Trophy className="h-16 w-16 text-slate-500 mx-auto mb-4" />
          <p className="text-lg font-bold text-white">No Recognition Data Yet</p>
          <p className="text-slate-400">Add productivity data in the Data Admin Panel to see team leaders.</p>
        </CardContent>
      </Card>
    );
  }

  const topLeader = leaders.leaders[0];

  return (
    <div className="space-y-6">
      {/* Header Stats with Futuristic Design */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card 
          className="relative overflow-hidden bg-slate-900/80 backdrop-blur-xl border-amber-500/30"
          style={{ boxShadow: '0 0 20px rgba(251, 191, 36, 0.2)' }}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-r from-amber-400 to-orange-500 rounded-xl text-white shadow-lg shadow-amber-500/30">
                <Trophy className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm text-amber-400 font-bold">Week</p>
                <p className="text-xl font-black text-white">{leaders.week}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className="relative overflow-hidden bg-slate-900/80 backdrop-blur-xl border-blue-500/30"
          style={{ boxShadow: '0 0 20px rgba(59, 130, 246, 0.2)' }}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl text-white shadow-lg shadow-blue-500/30">
                <Users className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm text-blue-400 font-bold">Team Size</p>
                <p className="text-xl font-black text-white">{leaders.total_clinicians} Clinicians</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className="relative overflow-hidden bg-slate-900/80 backdrop-blur-xl border-cyan-500/30"
          style={{ boxShadow: '0 0 20px rgba(6, 182, 212, 0.2)' }}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-r from-cyan-500 to-teal-500 rounded-xl text-white shadow-lg shadow-cyan-500/30">
                <BarChart3 className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm text-cyan-400 font-bold">Total Visits</p>
                <p className="text-xl font-black text-white">{leaders.team_total_visits}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className="relative overflow-hidden bg-slate-900/80 backdrop-blur-xl border-emerald-500/30"
          style={{ boxShadow: '0 0 20px rgba(16, 185, 129, 0.2)' }}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-r from-emerald-500 to-green-500 rounded-xl text-white shadow-lg shadow-emerald-500/30">
                <TrendingUp className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm text-emerald-400 font-bold">Team Avg</p>
                <p className="text-xl font-black text-white">{leaders.team_avg_productivity}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Featured Leader */}
      <Card className="bg-gradient-to-r from-yellow-400 via-amber-500 to-orange-500 text-white overflow-hidden">
        <CardContent className="p-6">
          <div className="flex items-center gap-6">
            <div className="relative">
              <div className="w-24 h-24 rounded-full bg-white/20 flex items-center justify-center text-5xl">
                🥇
              </div>
              <Crown className="absolute -top-2 -right-2 h-8 w-8 text-yellow-200" />
            </div>
            <div className="flex-1">
              <p className="text-amber-100 text-sm font-medium mb-1">🏆 This Week's Gold Star Leader</p>
              <h2 className="text-3xl font-bold mb-2">{topLeader.clinician_name}</h2>
              <div className="flex flex-wrap gap-3">
                <Badge className="bg-white/20 text-white">{topLeader.discipline}</Badge>
                <Badge className="bg-white/20 text-white">{topLeader.productivity_score}% Productivity</Badge>
                <Badge className="bg-white/20 text-white">{topLeader.visits_completed} Visits</Badge>
                <Badge className="bg-white/20 text-white">Score: {topLeader.leadership_score}</Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Leaderboard */}
      <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white font-bold">
            <Medal className="h-5 w-5 text-amber-400" />
            Weekly Leaderboard
          </CardTitle>
          <CardDescription className="text-slate-400">Top performers based on visits completed, capacity utilization, and efficiency</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {leaders.leaders.map((leader, idx) => (
              <div 
                key={idx}
                className={`flex items-center gap-4 p-4 rounded-xl border transition-all ${
                  idx === 0 ? 'bg-gradient-to-r from-amber-500/20 to-amber-600/10 border-amber-500/30' :
                  idx === 1 ? 'bg-gradient-to-r from-slate-400/20 to-slate-500/10 border-slate-500/30' :
                  idx === 2 ? 'bg-gradient-to-r from-orange-500/20 to-orange-600/10 border-orange-500/30' :
                  'bg-slate-800/50 border-slate-700/30'
                }`}
                style={{ boxShadow: idx === 0 ? '0 0 20px rgba(251, 191, 36, 0.2)' : 'none' }}
              >
                {/* Rank */}
                <div className="text-3xl font-bold w-12 text-center">
                  {leader.badge}
                </div>
                
                {/* Avatar */}
                <Avatar className="h-12 w-12">
                  <AvatarFallback className={`text-white font-bold ${
                    idx === 0 ? 'bg-amber-500' :
                    idx === 1 ? 'bg-slate-500' :
                    idx === 2 ? 'bg-orange-500' :
                    'bg-blue-500'
                  }`}>
                    {leader.clinician_name.split(' ').map(n => n[0]).join('')}
                  </AvatarFallback>
                </Avatar>
                
                {/* Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-lg text-white">{leader.clinician_name}</span>
                    <Badge variant="outline" className="border-slate-600 text-slate-400">{leader.discipline}</Badge>
                  </div>
                  <RecognitionBadge tier={leader.recognition_tier} />
                </div>
                
                {/* Visit Stats */}
                <div className="text-center px-4 border-l border-slate-700">
                  <div className="text-xl font-bold text-cyan-400">{leader.visits_completed}</div>
                  <div className="text-xs text-slate-500">of {leader.visit_capacity}</div>
                  <div className="text-xs text-slate-400 font-medium">Visits</div>
                </div>
                
                {/* Patients */}
                <div className="text-center px-4 border-l border-slate-700">
                  <div className="text-xl font-bold text-emerald-400">{leader.patients_assigned}</div>
                  <div className="text-xs text-slate-400 font-medium">Patients</div>
                </div>
                
                {/* Productivity Score */}
                <div className="text-right">
                  <div className="text-2xl font-black text-white">{leader.productivity_score}%</div>
                  <div className="text-xs text-slate-400">Productivity</div>
                  <Badge className={`mt-1 text-xs ${
                    leader.leadership_score >= 60 ? 'bg-emerald-500/20 text-emerald-400' :
                    leader.leadership_score >= 50 ? 'bg-blue-500/20 text-blue-400' :
                    'bg-slate-500/20 text-slate-400'
                  }`}>
                    Score: {leader.leadership_score}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Recognition History Component
const RecognitionHistory = ({ token }) => {
  const { data, loading, refetch } = useApiData('/api/dashboard/recognition/history?weeks=12', {
    cache: true,
    cacheKey: 'recognition_history'
  });
  
  const history = data?.history || [];

  if (loading) return <Skeleton className="h-96 w-full" />;

  return (
    <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700/50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-white font-bold">
          <Calendar className="h-5 w-5 text-rose-400" />
          Recognition History
        </CardTitle>
        <CardDescription className="text-slate-400">Weekly Gold Star leaders over the past weeks</CardDescription>
      </CardHeader>
      <CardContent>
        {history.length === 0 ? (
          <div className="text-center py-8">
            <Calendar className="h-12 w-12 text-slate-500 mx-auto mb-3" />
            <p className="text-slate-400 font-medium">No recognition history yet</p>
            <p className="text-slate-500 text-sm mt-1">Add productivity data to see weekly leaders</p>
          </div>
        ) : (
          <div className="space-y-3">
            {history.map((week, idx) => (
              <div 
                key={idx} 
                className="flex items-center gap-4 p-4 rounded-xl border border-slate-700/50 bg-slate-800/50 hover:bg-slate-800 transition-all"
                style={{ boxShadow: idx === 0 ? '0 0 15px rgba(251, 191, 36, 0.2)' : 'none' }}
              >
                <div className="text-2xl">{idx === 0 ? '🏆' : '📅'}</div>
                <div className="flex-1">
                  <p className="font-bold text-white">{week.week}</p>
                  <div className="flex gap-3 text-sm text-slate-400">
                    <span>{week.total_clinicians} clinicians</span>
                    <span>•</span>
                    <span>{week.team_avg}% team avg</span>
                  </div>
                </div>
                {week.top_leader && (
                  <div className="text-right">
                    <p className="font-bold text-white">{week.top_leader.clinician_name}</p>
                    <div className="flex items-center gap-2 justify-end mt-1">
                      <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                        {week.top_leader.visits_completed} visits
                      </Badge>
                      <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                        {week.top_leader.productivity_score}%
                      </Badge>
                    </div>
                  </div>
                )}
                <ChevronRight className="h-5 w-5 text-slate-500" />
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// All-Time Leaderboard Component
const AllTimeLeaderboard = ({ token }) => {
  const { data, loading, refetch } = useApiData('/api/dashboard/recognition/leaderboard', {
    cache: true,
    cacheKey: 'all_time_leaderboard'
  });
  
  const leaderboard = data?.leaderboard || [];

  if (loading) return <Skeleton className="h-96 w-full" />;

  return (
    <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700/50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-white font-bold">
          <Award className="h-5 w-5 text-violet-400" />
          All-Time Leaderboard
        </CardTitle>
        <CardDescription className="text-slate-400">Cumulative recognition scores across all weeks</CardDescription>
      </CardHeader>
      <CardContent>
        {leaderboard.length === 0 ? (
          <div className="text-center py-8">
            <Award className="h-12 w-12 text-slate-500 mx-auto mb-3" />
            <p className="text-slate-400 font-medium">No leaderboard data yet</p>
            <p className="text-slate-500 text-sm mt-1">Recognition accumulates over time</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left py-3 px-2 font-bold text-slate-300">Rank</th>
                  <th className="text-left py-3 px-2 font-bold text-slate-300">Clinician</th>
                  <th className="text-center py-3 px-2 font-bold text-slate-300">🥇</th>
                  <th className="text-center py-3 px-2 font-bold text-slate-300">🥈</th>
                  <th className="text-center py-3 px-2 font-bold text-slate-300">🥉</th>
                  <th className="text-right py-3 px-2 font-bold text-slate-300">Avg %</th>
                  <th className="text-right py-3 px-2 font-bold text-slate-300">Score</th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.map((entry, idx) => (
                  <tr 
                    key={idx} 
                    className={`border-b border-slate-800 ${
                      idx === 0 ? 'bg-amber-500/10' : 
                      idx === 1 ? 'bg-slate-400/10' : 
                      idx === 2 ? 'bg-orange-500/10' : ''
                    }`}
                  >
                    <td className="py-3 px-2">
                      <span className={`
                        ${idx === 0 ? 'text-2xl' : idx === 1 ? 'text-xl' : idx === 2 ? 'text-lg' : 'text-base text-slate-400'}
                        font-bold
                      `}>
                        {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${entry.rank}`}
                      </span>
                    </td>
                    <td className="py-3 px-2">
                      <div className="flex items-center gap-2">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className={`text-white text-xs ${
                            idx === 0 ? 'bg-amber-500' : 
                            idx === 1 ? 'bg-slate-500' : 
                            idx === 2 ? 'bg-orange-500' : 'bg-blue-500'
                          }`}>
                            {entry.clinician_name.split(' ').map(n => n[0]).join('')}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-bold text-white">{entry.clinician_name}</p>
                          <Badge variant="outline" className="text-xs border-slate-600 text-slate-400">{entry.discipline}</Badge>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-2 text-center font-bold text-amber-400">{entry.gold_stars}</td>
                    <td className="py-3 px-2 text-center font-bold text-slate-400">{entry.silver_awards}</td>
                    <td className="py-3 px-2 text-center font-bold text-orange-400">{entry.bronze_awards}</td>
                    <td className="py-3 px-2 text-right">
                      <Badge className={`${
                        entry.avg_productivity >= 90 ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 
                        entry.avg_productivity >= 80 ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' : 
                        'bg-slate-500/20 text-slate-400 border-slate-500/30'
                      }`}>
                        {entry.avg_productivity}%
                      </Badge>
                    </td>
                    <td className="py-3 px-2 text-right font-bold text-lg text-white">{entry.score.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Team Performance Trends Component
const TeamPerformanceTrends = ({ token }) => {
  const { data, loading } = useApiData('/api/dashboard/recognition/history?weeks=12', {
    cache: true,
    cacheKey: 'recognition_trends'
  });
  
  const history = data?.history || [];
  
  // Transform data for charts
  const chartData = [...history].reverse().map(week => ({
    week: week.week?.substring(5) || '', // Show MM-DD format
    fullWeek: week.week,
    teamAvg: week.team_avg || 0,
    clinicians: week.total_clinicians || 0,
    topScore: week.top_leader?.productivity_score || 0,
    topVisits: week.top_leader?.visits_completed || 0,
    topLeader: week.top_leader?.clinician_name || 'N/A'
  }));
  
  // Calculate summary stats
  const avgProductivity = chartData.length > 0 
    ? (chartData.reduce((sum, d) => sum + d.teamAvg, 0) / chartData.length).toFixed(1) 
    : 0;
  const totalVisits = chartData.reduce((sum, d) => sum + d.topVisits, 0);
  const trendDirection = chartData.length >= 2 
    ? chartData[chartData.length - 1].teamAvg - chartData[0].teamAvg 
    : 0;

  if (loading) return <Skeleton className="h-96 w-full" />;

  if (chartData.length === 0) {
    return (
      <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700/50">
        <CardContent className="p-12 text-center">
          <Activity className="h-16 w-16 text-slate-500 mx-auto mb-4" />
          <p className="text-lg font-bold text-white">No Trend Data Yet</p>
          <p className="text-slate-400">Add more weeks of productivity data to see trends.</p>
        </CardContent>
      </Card>
    );
  }

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-3 shadow-xl">
          <p className="text-white font-bold mb-2">{payload[0]?.payload?.fullWeek}</p>
          {payload.map((entry, idx) => (
            <p key={idx} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value}{entry.name.includes('%') || entry.name.includes('Avg') ? '%' : ''}
            </p>
          ))}
          {payload[0]?.payload?.topLeader && (
            <p className="text-xs text-slate-400 mt-1">Leader: {payload[0].payload.topLeader}</p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card 
          className="bg-slate-900/80 backdrop-blur-xl border-cyan-500/30"
          style={{ boxShadow: '0 0 20px rgba(6, 182, 212, 0.2)' }}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-r from-cyan-500 to-teal-500 rounded-xl text-white">
                <Activity className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm text-cyan-400 font-bold">Avg Productivity</p>
                <p className="text-xl font-black text-white">{avgProductivity}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className="bg-slate-900/80 backdrop-blur-xl border-emerald-500/30"
          style={{ boxShadow: '0 0 20px rgba(16, 185, 129, 0.2)' }}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-r from-emerald-500 to-green-500 rounded-xl text-white">
                <TrendingUp className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm text-emerald-400 font-bold">Trend</p>
                <p className={`text-xl font-black ${trendDirection >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {trendDirection >= 0 ? '↑' : '↓'} {Math.abs(trendDirection).toFixed(1)}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className="bg-slate-900/80 backdrop-blur-xl border-violet-500/30"
          style={{ boxShadow: '0 0 20px rgba(139, 92, 246, 0.2)' }}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-r from-violet-500 to-purple-500 rounded-xl text-white">
                <BarChart3 className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm text-violet-400 font-bold">Total Visits</p>
                <p className="text-xl font-black text-white">{totalVisits}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className="bg-slate-900/80 backdrop-blur-xl border-amber-500/30"
          style={{ boxShadow: '0 0 20px rgba(251, 191, 36, 0.2)' }}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl text-white">
                <Calendar className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm text-amber-400 font-bold">Weeks Tracked</p>
                <p className="text-xl font-black text-white">{chartData.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Team Productivity Trend Chart */}
      <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white font-bold">
            <TrendingUp className="h-5 w-5 text-cyan-400" />
            Team Productivity Trend
          </CardTitle>
          <CardDescription className="text-slate-400">
            Weekly team average productivity and top performer scores
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="teamAvgGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="topScoreGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#fbbf24" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#fbbf24" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="week" stroke="#94a3b8" fontSize={12} />
                <YAxis stroke="#94a3b8" fontSize={12} domain={[0, 100]} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="teamAvg" 
                  name="Team Avg %" 
                  stroke="#06b6d4" 
                  strokeWidth={2}
                  fill="url(#teamAvgGradient)" 
                />
                <Area 
                  type="monotone" 
                  dataKey="topScore" 
                  name="Top Score %" 
                  stroke="#fbbf24" 
                  strokeWidth={2}
                  fill="url(#topScoreGradient)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Visits Completed Bar Chart */}
      <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white font-bold">
            <BarChart3 className="h-5 w-5 text-emerald-400" />
            Weekly Top Performer Visits
          </CardTitle>
          <CardDescription className="text-slate-400">
            Number of visits completed by weekly Gold Star leaders
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="week" stroke="#94a3b8" fontSize={12} />
                <YAxis stroke="#94a3b8" fontSize={12} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar 
                  dataKey="topVisits" 
                  name="Visits Completed" 
                  fill="#10b981" 
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Team Size Over Time */}
      <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white font-bold">
            <Users className="h-5 w-5 text-violet-400" />
            Team Size Trend
          </CardTitle>
          <CardDescription className="text-slate-400">
            Number of active clinicians per week
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="week" stroke="#94a3b8" fontSize={12} />
                <YAxis stroke="#94a3b8" fontSize={12} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="clinicians" 
                  name="Clinicians" 
                  stroke="#8b5cf6" 
                  strokeWidth={3}
                  dot={{ fill: '#8b5cf6', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, fill: '#a78bfa' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Main Team Recognition Page
export default function TeamRecognition() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('weekly');

  return (
    <div className="space-y-6" data-testid="team-recognition-page">
      {/* Futuristic Neon Header - Golden/Amber Theme */}
      <div className="relative overflow-hidden rounded-3xl p-8 text-white">
        {/* Animated gradient background */}
        <div className="absolute inset-0 bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 animate-gradient-x" />
        
        {/* Mesh overlay */}
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, white 1px, transparent 1px),
                            radial-gradient(circle at 75% 75%, white 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />
        
        {/* Glow effects */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-white/15 rounded-full blur-3xl" />

        <div className="relative z-10">
          <h1 className="text-4xl font-black tracking-tight flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-sm shadow-lg" style={{ boxShadow: '0 0 30px rgba(255,255,255,0.3)' }}>
              <Trophy className="h-8 w-8" style={{ filter: 'drop-shadow(0 0 10px white)' }} />
            </div>
            <span className="drop-shadow-lg" style={{ textShadow: '0 0 40px rgba(255,255,255,0.5)' }}>
              Team Leader Recognition
            </span>
          </h1>
          <p className="text-white/80 mt-3 text-lg font-medium max-w-xl">
            Celebrating excellence in patient care and productivity
          </p>
        </div>
      </div>

      {/* Data Fix Toolbar */}
      <DataFixToolbar 
        onDataFixed={() => window.location.reload()} 
        variant="compact"
      />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap h-auto gap-2 p-2 bg-slate-900/60 backdrop-blur-xl rounded-2xl border border-slate-700/50">
          <TabsTrigger value="weekly" className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-amber-500 data-[state=active]:to-orange-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-amber-500/30 rounded-xl px-4 py-2 font-bold transition-all">
            <Star className="h-4 w-4" /> This Week
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-rose-500 data-[state=active]:to-pink-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-rose-500/30 rounded-xl px-4 py-2 font-bold transition-all">
            <Calendar className="h-4 w-4" /> History
          </TabsTrigger>
          <TabsTrigger value="trends" className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-cyan-500 data-[state=active]:to-teal-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-cyan-500/30 rounded-xl px-4 py-2 font-bold transition-all">
            <Activity className="h-4 w-4" /> Trends
          </TabsTrigger>
          <TabsTrigger value="leaderboard" className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-violet-500 data-[state=active]:to-purple-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-violet-500/30 rounded-xl px-4 py-2 font-bold transition-all">
            <Award className="h-4 w-4" /> All-Time
          </TabsTrigger>
        </TabsList>

        <TabsContent value="weekly">
          <WeeklyLeaders token={token} />
        </TabsContent>

        <TabsContent value="history">
          <RecognitionHistory token={token} />
        </TabsContent>

        <TabsContent value="trends">
          <TeamPerformanceTrends token={token} />
        </TabsContent>

        <TabsContent value="leaderboard">
          <AllTimeLeaderboard token={token} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
